import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Sayfa yüklendiğinde token varsa backend'den güncel user bilgilerini çek
    const checkAuth = async () => {
      // localStorage veya sessionStorage'dan token kontrolü
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      
      // Optimistic loading: Eğer storage'da user varsa önce onu göster
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (e) {
          console.error('Stored user parse hatası:', e);
        }
      }
      
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Backend'den güncel user bilgilerini al
          const { data } = await axios.get('/api/auth/me');
          
          // Token'ın hangi storage'da olduğunu kontrol et ve oraya kaydet
          if (localStorage.getItem('token')) {
            localStorage.setItem('user', JSON.stringify(data));
            localStorage.setItem('token', token);
          } else {
            sessionStorage.setItem('user', JSON.stringify(data));
            sessionStorage.setItem('token', token);
          }
          setUser(data);
        } catch (error) {
          console.error('Token doğrulama hatası:', error);
          // Token geçersizse temizle
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
      } else {
        // Token yoksa user'ı da temizle
        setUser(null);
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const { data } = await axios.post('/api/auth/login', { email, password });
      
      // "Beni Unutma" işaretliyse localStorage, değilse sessionStorage kullan
      const storage = rememberMe ? localStorage : sessionStorage;
      
      storage.setItem('user', JSON.stringify(data));
      storage.setItem('token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Giriş başarısız' 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post('/api/auth/register', { name, email, password });
      
      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Kayıt başarısız' 
      };
    }
  };

  const logout = () => {
    // Her iki storage'dan da temizle
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    setUser,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

